import React, { Component, JSXElementConstructor } from "react";
import { RepaymentCalculator } from "../../lib/CmsApi";
import TopBox from "../common/TopBox";
import {
  calcTilgungsPlan,
  PlanRate,
  TilgungFormData,
  TilgungsReturnData
} from "../../lib/Tilgungsplanrechner";
import FormBox from "../form/FormBox";
import Content from "../common/Content";
import buildForm, { Props } from "../form/buildForm";
import { Field } from "../form/input/Field";
import * as yup from "yup";
import Dropdown, { DropdownOption } from "../form/input/Dropdown";
import PageNotFoundError from "../../lib/pageNotFoundError";
import { TableBox, TD, TH, TR } from "../sections/Table";
import ResultView from "./ResultView";
import { yearOrYears } from "../../lib/util";
import { spacing } from "../theme";
import styled from "styled-components";
type State = {
  choosenProgram: RecalculatorProgram | null;
  calc?: TilgungsReturnData;
};

const Main = styled.main`
  ${spacing("padding-bottom", 80)}
`;
const ContentHiddenOnPrint = styled(Content)`
  @media print {
    display: none;
  }
`;

export type RecalculatorProgram = {
  name: string;
  rates: {
    years: number;
    rate: number;
    isDefault?: boolean;
  }[];
  loan?: {
    from: number;
    to: number;
    defaultValue?: number;
  };
  time?: {
    from: number;
    to: number;
    defaultValue?: number;
  };
  graceTime?: {
    from: number;
    to: number;
    defaultValue?: number;
  };
};

export default class Tilgungsrechner extends Component<
  RepaymentCalculator,
  State
> {
  form: JSXElementConstructor<Props<TilgungFormData>>;
  constructor(props, ctx) {
    super(props, ctx);
    this.form = buildForm({
      initialValues: {
        program: "none",
        darlBetrag: 0,
        zinssatz: "",
        zinsbindung: "none",
        laufzeit: "none",
        tilgunsfreieJahre: "none"
      },
      fieldSchema: {
        program: yup
          .string()
          .required("Pflichtfeld")
          .notOneOf(["none"], "Bitte auswählen"),
        darlBetrag: yup.number().required("Pflichtfeld"),
        zinssatz: yup.string().required("Wird automatisch ausgefüllt"),
        zinsbindung: yup
          .string()
          .required("Pflichtfeld")
          .notOneOf(["none"], "Bitte auswählen"),
        laufzeit: yup
          .string()
          .required("Pflichtfeld")
          .notOneOf(["none"], "Bitte auswählen"),
        tilgunsfreieJahre: yup
          .string()
          .required("Pflichtfeld")
          .notOneOf(["none"], "Bitte auswählen")
      },
      handleSubmit: async values => {
        const calc = await calcTilgungsPlan({
          program: values.program,
          darlBetrag: values.darlBetrag,
          zinssatz: parseFloat(values.zinssatz),
          laufzeit: parseFloat(values.laufzeit),
          zinsbindung: parseFloat(values.zinsbindung),
          tilgunsfreieJahre: parseFloat(values.tilgunsfreieJahre)
        });
        this.setState({
          calc
        });
      },
      validate: values => {
        const state = this.state;
        let errors: any = {};
        if (state.choosenProgram) {
          if (
            state.choosenProgram.loan.from > values.darlBetrag ||
            state.choosenProgram.loan.to < values.darlBetrag
          ) {
            errors.darlBetrag = `Der Darlehensbetrag muss zwischen ${state.choosenProgram.loan.from.toLocaleString(
              "de",
              {
                style: "currency",
                currency: "EUR",
                currencyDisplay: "symbol"
              }
            )} und ${state.choosenProgram.loan.to.toLocaleString("de", {
              style: "currency",
              currency: "EUR",
              currencyDisplay: "symbol"
            })} liegen`;
          }
        }
        return errors;
      },
      handleReset: () => this.setState({ choosenProgram: null, calc: null }),
      noValidate: true
    });
  }
  state: State = {
    choosenProgram: null,
    calc: null
  };
  render() {
    let { title, description, programs } = this.props;
    const Form = this.form;
    const state = this.state;
    if (!programs || programs.length <= 1) {
      throw new PageNotFoundError();
      return;
    }
    return (
      <>
        <TopBox
          title={title}
          breadCrumpPath={[{ route: "tilgungsrechner", title: title }]}
          intro={description}
          infoBox={<div />}
        />
        <Main>
          <ContentHiddenOnPrint padded>
            <FormBox>
              <Form submitLabel="Berechnen">
                {f => {
                  return (
                    <>
                      <Field
                        name="program"
                        label="Programm*"
                        component={Dropdown}
                        onChange={e => {
                          f.setFieldTouched(e.target.name, true);
                          f.handleChange(e);
                          const choosenProgram = programs.find(
                            prog => prog.name === e.target.value
                          );
                          this.setState({
                            choosenProgram: choosenProgram
                              ? choosenProgram
                              : null
                          });
                          if (
                            choosenProgram.loan.defaultValue &&
                            choosenProgram.loan.defaultValue <=
                              choosenProgram.loan.to &&
                            choosenProgram.loan.defaultValue >=
                              choosenProgram.loan.from
                          ) {
                            f.setFieldValue(
                              "darlBetrag",
                              choosenProgram.loan.defaultValue
                            );
                          } else {
                            f.setFieldValue("darlBetrag", "");
                          }
                          if (choosenProgram.rates) {
                            const defaultZinsBindung = choosenProgram.rates.find(
                              r => r.isDefault
                            );
                            if (defaultZinsBindung) {
                              f.setFieldValue(
                                "zinsbindung",
                                defaultZinsBindung.years
                              );
                              f.setFieldValue(
                                "zinssatz",
                                defaultZinsBindung.rate + " %"
                              );
                            } else {
                              f.setFieldValue("zinsbindung", "none");
                              f.setFieldValue("zinssatz", "");
                            }
                          }
                          if (
                            choosenProgram.time.defaultValue &&
                            choosenProgram.time.defaultValue <=
                              choosenProgram.time.to &&
                            choosenProgram.time.defaultValue >=
                              choosenProgram.time.from
                          ) {
                            f.setFieldValue(
                              "laufzeit",
                              choosenProgram.time.defaultValue
                            );
                          } else {
                            f.setFieldValue("laufzeit", "none");
                          }
                          if (
                            choosenProgram.graceTime.defaultValue &&
                            choosenProgram.graceTime.defaultValue <=
                              choosenProgram.graceTime.to &&
                            choosenProgram.graceTime.defaultValue >=
                              choosenProgram.graceTime.from
                          ) {
                            f.setFieldValue(
                              "tilgunsfreieJahre",
                              choosenProgram.graceTime.defaultValue
                            );
                          } else {
                            f.setFieldValue("tilgunsfreieJahre", "none");
                          }
                        }}
                      >
                        <DropdownOption value="none" disabled>
                          Programm wählen
                        </DropdownOption>
                        {programs.map((p, i) => (
                          <DropdownOption key={i}>{p.name}</DropdownOption>
                        ))}
                      </Field>

                      <Field
                        name="zinsbindung"
                        label="Zinsbindung*"
                        component={Dropdown}
                        disabled={!state.choosenProgram}
                        onChange={e => {
                          if (!state.choosenProgram) {
                            return;
                          }
                          f.setFieldTouched(e.target.name, true);
                          f.handleChange(e);
                          const val = parseInt(e.target.value);
                          const choosenRate = state.choosenProgram.rates.find(
                            r => r.years === val
                          );
                          f.setFieldValue("zinssatz", choosenRate.rate + " %");
                        }}
                      >
                        <DropdownOption value="none" disabled>
                          Laufzeit wählen
                        </DropdownOption>
                        {state.choosenProgram &&
                          state.choosenProgram.rates.map((r, i) => (
                            <DropdownOption key={i} value={r.years}>
                              {r.years + " " + yearOrYears(r.years)}
                            </DropdownOption>
                          ))}
                      </Field>
                      <Field
                        name="zinssatz"
                        type="text"
                        label="Zinssatz*"
                        readOnly
                        disabled={!state.choosenProgram}
                      />
                      <Field
                        name="darlBetrag"
                        type="number"
                        label="Darlehensbetrag*"
                        info={
                          !state.choosenProgram
                            ? " "
                            : `Der Darlehensbetrag muss zwischen ${state.choosenProgram.loan.from.toLocaleString(
                                "de",
                                {
                                  style: "currency",
                                  currency: "EUR",
                                  currencyDisplay: "symbol"
                                }
                              )} und ${state.choosenProgram.loan.to.toLocaleString(
                                "de",
                                {
                                  style: "currency",
                                  currency: "EUR",
                                  currencyDisplay: "symbol"
                                }
                              )} liegen`
                        }
                        placeholder={"Betrag eingeben"}
                        disabled={!state.choosenProgram}
                        min={
                          state.choosenProgram
                            ? state.choosenProgram.loan.from
                            : 0
                        }
                        max={
                          state.choosenProgram
                            ? state.choosenProgram.loan.to
                            : 0
                        }
                      />
                      <Field
                        name="laufzeit"
                        label="Darlehenslaufzeit*"
                        component={Dropdown}
                        disabled={!state.choosenProgram}
                      >
                        <DropdownOption value="none" disabled>
                          Laufzeit wählen
                        </DropdownOption>
                        {state.choosenProgram &&
                          state.choosenProgram.time &&
                          Array(
                            state.choosenProgram.time.to -
                              state.choosenProgram.time.from +
                              1
                          )
                            .fill(null)
                            .map((r, i) => (
                              <DropdownOption
                                key={i}
                                value={state.choosenProgram.time.from + i}
                              >
                                {state.choosenProgram.time.from +
                                  i +
                                  " " +
                                  yearOrYears(
                                    state.choosenProgram.time.from + i
                                  )}
                              </DropdownOption>
                            ))}
                      </Field>
                      <Field
                        name="tilgunsfreieJahre"
                        label="Tilgungsfreie Anlaufjahre*"
                        component={Dropdown}
                        disabled={!state.choosenProgram}
                      >
                        <DropdownOption value="none" disabled>
                          Laufzeit wählen
                        </DropdownOption>
                        {state.choosenProgram &&
                          state.choosenProgram.graceTime &&
                          Array(
                            state.choosenProgram.graceTime.to -
                              state.choosenProgram.graceTime.from +
                              1
                          )
                            .fill(null)
                            .map((r, i) => (
                              <DropdownOption
                                key={i}
                                value={state.choosenProgram.graceTime.from + i}
                              >
                                {state.choosenProgram.graceTime.from +
                                  i +
                                  " " +
                                  yearOrYears(
                                    state.choosenProgram.graceTime.from + i
                                  )}
                              </DropdownOption>
                            ))}
                      </Field>
                    </>
                  );
                }}
              </Form>
            </FormBox>
          </ContentHiddenOnPrint>
          {state.calc && state.choosenProgram && (
            <ResultView
              calc={state.calc}
              choosenProgram={state.choosenProgram}
            />
          )}
        </Main>
      </>
    );
  }
}
